import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Space, Table, type TableColumnType, type TableProps, Typography, theme } from 'antd';
import {
  ArrowRightOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';

import {
  buildDiagnosticsReportViewedProperties,
  createDiagnosticsRequestId,
} from '../DiagnosticsAnalyticsEvents';
import { AiInsightsEmptyState } from '../../shared';
import { type DiagnosticsReportListItem, useGetDiagnosticsReportsQuery } from '../api/diagnosticsReportApi';
import RerunPlanGate from './RerunPlanGate';
import { PointInTime, POINT_IN_TIME_COLUMN_WIDTH, pointInTimeCell } from '~components/PointInTime';
import { extractDomain, getBlockedCrawlersDisplayText } from '../diagnosticsReportUtils';
import useExportReport from '../hooks/useExportReport';
import useTrackDiagnosticsEvent from '../hooks/useTrackDiagnosticsEvent';
import {
  buildDiagnosticsQueryParameters,
  QueryParamNames,
  type DiagnosticsQueryParams,
} from '../DiagnosticsQueryParameters';
import useQueryStringPagination from '../../../../utils/useQueryStringPagination';
import useQueryStringParameters from '../../../../utils/useQueryStringParameters';
import { useGetDomainsQuery } from '~features/domain-manager/api/domainManagerApiSlice';
import { CustomTooltip } from '~components/Tooltip';
import DomainDropdownFilter from '~components/dropdownFilters/DomainDropdownFilter';

type DiagnosticsReportsTableProps = {
  onRerun: (domain: string, requestId: string) => void;
};

const PAGE_SIZE = 10;

const DOMAIN_COLUMN_KEY = 'url';

const VIEW_REPORT_LABEL = 'View report';
const DOWNLOAD_REPORT_LABEL = 'Download report';

const COLUMN_TO_SORT_FIELD: Record<string, string> = {
  createdAt: 'createdAt',
  url: 'domain',
  blockedCrawlers: 'blockedCrawlers',
};

export default function DiagnosticsReportsTable({ onRerun }: DiagnosticsReportsTableProps) {
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const exportReport = useExportReport();
  const trackDiagnosticsEvent = useTrackDiagnosticsEvent();
  const { pagination, updatePagination } = useQueryStringPagination(PAGE_SIZE);
  const { queryStrings, updateMultipleParameters } =
    useQueryStringParameters<DiagnosticsQueryParams>(buildDiagnosticsQueryParameters);
  const domainsResult = useGetDomainsQuery('');
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | undefined>(undefined);

  const selectedDomains = queryStrings[QueryParamNames.Domain];

  const { data: page, isFetching } = useGetDiagnosticsReportsQuery({
    page: pagination.page,
    pageSize: pagination.pageSize,
    sortBy,
    sortDirection,
    domains: selectedDomains,
  });

  const availableDomains = useMemo(
    () => (domainsResult.data?.domains ?? []).map(({ domain }) => domain),
    [domainsResult.data?.domains]
  );

  const handleTableChange = useCallback<NonNullable<TableProps<DiagnosticsReportListItem>['onChange']>>(
    (_pagination, filters, sorter, extra) => {
      if (extra.action === 'paginate') return;

      const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
      const columnKey = String(singleSorter.columnKey ?? singleSorter.field ?? '');
      const field = COLUMN_TO_SORT_FIELD[columnKey];

      if (!singleSorter.order || !field) {
        setSortBy(undefined);
        setSortDirection(undefined);
      } else {
        setSortBy(field);
        setSortDirection(singleSorter.order === 'ascend' ? 'asc' : 'desc');
      }

      const domains = (filters[DOMAIN_COLUMN_KEY] as string[] | null) ?? undefined;
      updateMultipleParameters({ domain: domains });
    },
    [updateMultipleParameters]
  );

  const columns: TableColumnType<DiagnosticsReportListItem>[] = [
    {
      title: 'Creation Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      defaultSortOrder: 'descend',
      width: POINT_IN_TIME_COLUMN_WIDTH,
      onCell: pointInTimeCell,
      render: (value: string) => <PointInTime value={value} />,
    },
    {
      title: 'Domain',
      dataIndex: 'url',
      key: DOMAIN_COLUMN_KEY,
      sorter: true,
      filteredValue: selectedDomains ?? null,
      filterDropdown: ({ confirm, clearFilters, selectedKeys, setSelectedKeys }) => (
        <DomainDropdownFilter
          confirm={confirm}
          clearFilters={clearFilters}
          selectedKeys={selectedKeys}
          setSelectedKeys={setSelectedKeys}
          domains={availableDomains}
          loading={domainsResult.isLoading}
        />
      ),
      filterDropdownProps: { destroyOnHidden: true },
      render: (value: string) => {
        const domain = extractDomain(value);
        return (
          <Typography.Link
            href={`https://${domain}`}
            target="_blank"
            rel="noreferrer"
            style={{ color: token.colorPrimary }}
          >
            {domain}
          </Typography.Link>
        );
      },
    },
    {
      title: 'Blocked AI Crawlers',
      key: 'blockedCrawlers',
      sorter: true,
      width: 264,
      render: (_: unknown, record: DiagnosticsReportListItem) => {
        const text = getBlockedCrawlersDisplayText(record.userAgentResults, record.status);

        if (record.status === 'partial') {
          return (
            <Space size={token.marginXXS}>
              <span>{text}</span>
              <CustomTooltip placement="top" message="Some checks failed to run">
                <ExclamationCircleOutlined style={{ color: token.colorWarning }} />
              </CustomTooltip>
            </Space>
          );
        }

        return text;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 111,
      align: 'center',
      render: (_: unknown, record: DiagnosticsReportListItem) => {
        const domain = extractDomain(record.url);

        if (record.status === 'error') {
          return (
            <RerunPlanGate
              render={(rerunDisabled) => (
                <Button
                  type="link"
                  size="small"
                  icon={<SyncOutlined />}
                  disabled={rerunDisabled}
                  onClick={() => {
                    const requestId = createDiagnosticsRequestId();
                    trackDiagnosticsEvent('Diagnostics: Run Clicked', {
                      domain,
                      bots_to_test: '',
                      run_trigger: 'diagnostics_report_table',
                      request_id: requestId,
                    });
                    onRerun(domain, requestId);
                  }}
                >
                  Rerun
                </Button>
              )}
            />
          );
        }

        return (
          <Space size={token.marginXS}>
            <CustomTooltip placement="top" message={VIEW_REPORT_LABEL}>
              <Button
                type="link"
                size="small"
                icon={<ArrowRightOutlined />}
                aria-label={VIEW_REPORT_LABEL}
                onClick={() => {
                  trackDiagnosticsEvent('Diagnostics: Report Clicked', {
                    ...buildDiagnosticsReportViewedProperties(record),
                  });
                  navigate(`/ai-insights/diagnostics/report/${record.id}`);
                }}
              />
            </CustomTooltip>
            <CustomTooltip placement="top" message={DOWNLOAD_REPORT_LABEL}>
              <Button
                type="link"
                size="small"
                icon={<DownloadOutlined />}
                aria-label={DOWNLOAD_REPORT_LABEL}
                onClick={() => exportReport(record.id, 'report_table')}
              />
            </CustomTooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <Card size="small">
      <Table<DiagnosticsReportListItem>
        size="small"
        columns={columns}
        dataSource={page?.items}
        loading={isFetching}
        rowKey="id"
        pagination={{
          onChange: (newPage, newPageSize) => {
            if (newPageSize !== pagination.pageSize) updatePagination(1, newPageSize);
            else updatePagination(newPage, newPageSize);
          },
          current: pagination.page,
          pageSize: pagination.pageSize,
          total: page?.pageInfo.totalElements,
          showSizeChanger: { showSearch: false },
          pageSizeOptions: ['10', '20', '30', '50'],
        }}
        showSorterTooltip={false}
        onChange={handleTableChange}
        locale={{
          emptyText: (
            <AiInsightsEmptyState
              headline="No diagnostic reports yet"
              body="Once diagnostics are run, your reports will appear here."
            />
          ),
        }}
      />
    </Card>
  );
}
